import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Car, Wrench, BatteryWarning, AlertTriangle, Fuel, ShieldCheck, Download } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import SidePanel from '../SidePanel';
import InspectorPrehled from '../flotila/InspectorPrehled';
import InspectorNastaveni from '../flotila/InspectorNastaveni';
import InspectorCreate from '../flotila/InspectorCreate';
import PojistniKarta from '../flotila/PojistniKarta';
import TankovaniModal from '../flotila/TankovaniModal';
import ServisModal from '../flotila/ServisModal';
import PojistniModal from '../flotila/PojistniModal';
import { classifyVozidlo } from '../flotila/VozidloDlazdice';
import { SECTION_COLOR, SECTION_BG, CERVENA, kDoreseniAut, nacti, kc, cislo, datum } from './ui';

/**
 * Rozvoz → Auta. Norma §1: seznam aut (široký = mřížka, úzký = dvouřádková
 * karta) · detail auta ve sloupci se záložkami Přehled / Tankování / Servis /
 * Pojištění / Nastavení · třetí sloupec `panelVzdy` s vozovým parkem a
 * „K dořešení" (STK, pojištění, servis, baterie — `kDoreseniAut`).
 *
 * Dřív tři samostatné záložky Flotily (Tankování, Servis, Pojištění) s
 * vlastními dlaždicemi a galerií karet; teď je to agenda jednoho auta.
 * Přidání auta = inspektor (§1f), záznam tankování / servisu / smlouvy = modal.
 */
const POHLEDY = [
    { id: 'vse', label: 'Vše', patri: () => true },
    { id: 'kdoreseni', label: 'K dořešení', upozorneni: true, patri: (v, kd) => kd.has(v.id) },
    { id: 'jede', label: 'Na cestě', patri: (v) => classifyVozidlo(v) === 'driving' },
    { id: 'servis', label: 'V servisu', patri: (v) => v.stav === 'servis' || v.aktivni_servis },
];
const STAV = { driving: 'Jede', idle: 'Stojí', parked: 'Zaparkováno' };
const ZALOZKY = [
    { id: 'prehled', label: 'Přehled' },
    { id: 'tankovani', label: 'Tankování' },
    { id: 'servis', label: 'Servis' },
    { id: 'pojisteni', label: 'Pojištění' },
    { id: 'nastaveni', label: 'Nastavení' },
];

const dniText = (d) => (d == null ? '—' : d < 0 ? `prošlo` : `${d} d`);

export default function RozvozAuta({ vozidla, refetch, ridiciMap, isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritId = null }) {
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(otevritId);
    const [zalozka, setZalozka] = useState('prehled');
    const [pridat, setPridat] = useState(false);
    const [modal, setModal] = useState(null);   // { druh: 'tankovani'|'servis'|'pojisteni', vozidlo }
    const [obnova, setObnova] = useState(0);
    useEffect(() => { if (otevritId) setVybranyId(otevritId); }, [otevritId]);
    useEffect(() => { setZalozka('prehled'); }, [vybranyId]);

    // Horní hrana obsahu pro inspektor (norma §1f)
    const obsahRef = useRef(null);
    const [top, setTop] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = obsahRef.current; if (el) setTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [pridat, vybranyId]);

    const kDoreseni = useMemo(() => kDoreseniAut(vozidla), [vozidla]);
    const sKDoreseni = useMemo(() => new Set(kDoreseni.map((k) => k.vozidloId)), [kDoreseni]);
    // Červeně v buňce jen jednoznačný problém — stejný úsudek jako „K dořešení" (norma §3)
    const problemy = useMemo(() => new Set(kDoreseni.filter((k) => k.problem).map((k) => k.id)), [kDoreseni]);
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map((p) => [p.id, vozidla.filter((v) => p.patri(v, sKDoreseni)).length])), [vozidla, sKDoreseni]);
    const akt = POHLEDY.find((p) => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const jmenoRidice = (v) => {
        const r = v.aktualni_ridic_id ? ridiciMap.get(v.aktualni_ridic_id) : null;
        return r ? (r.cele_jmeno || `${r.jmeno || ''} ${r.prijmeni || ''}`.trim()) : null;
    };
    const seznam = vozidla.filter((v) => akt.patri(v, sKDoreseni)
        && (!q || `${v.spz} ${v.model || ''} ${jmenoRidice(v) || ''}`.toLowerCase().includes(q)));
    const vybrane = vozidla.find((v) => v.id === vybranyId) || null;
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    // ── Export (nástroje modulu, ne položka seznamu) ──
    const stahniCsv = (nazev, hlavicka, radky) => {
        const csv = [hlavicka, ...radky].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
        a.download = nazev;
        a.click();
    };
    const rokZpet = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); };
    const exportTankovani = () => {
        const od = rokZpet(), dnes = new Date().toISOString().slice(0, 10);
        nacti(`/api/flotila/tankovani?datum_od=${od}&datum_do=${dnes}`).then((d) => stahniCsv(`tankovani-${od}_${dnes}.csv`,
            ['Datum', 'SPZ', 'Řidič', 'Pumpa', 'Litry', 'Cena/l', 'Celkem', 'Tachometr'],
            (Array.isArray(d) ? d : []).map((t) => [t.datum, t.vozidlo?.spz, t.zamestnanec ? `${t.zamestnanec.jmeno} ${t.zamestnanec.prijmeni}` : '',
                t.dodavatel?.nazev, t.litry, t.cena_kc_litr, (Number(t.litry) * Number(t.cena_kc_litr)).toFixed(2), t.km_stav]))).catch(() => {});
    };
    const exportServis = () => {
        nacti('/api/flotila/servis/feed').then((d) => stahniCsv('servis-a-stk.csv',
            ['Datum', 'SPZ', 'Typ', 'Popis', 'Servis', 'Tachometr', 'Cena', 'Stav'],
            (Array.isArray(d) ? d : []).map((s) => [s.datum, s.vozidlo?.spz, (s.typy || []).map((x) => x.nazev).join(', ') || s.typ, s.popis, s.servis, s.km_stav, s.cena_kc, s.stav]))).catch(() => {});
    };

    // ── Seznam ──
    const radek = (v, siroky) => {
        const sel = v.id === vybranyId;
        const st = classifyVozidlo(v);
        const ridic = jmenoRidice(v);
        const problem = kDoreseni.some((k) => k.vozidloId === v.id && k.problem);
        const bunka = (hodnota, spatne, sirka = 96) => (
            <span style={{ width: sirka, flexShrink: 0, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
                fontWeight: spatne ? 700 : 500, color: spatne ? CERVENA : 'var(--text-primary)' }}>{hodnota}</span>
        );
        return (
            <button key={v.id} type="button" onClick={() => { setPridat(false); setVybranyId(v.id); }} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, cursor: 'pointer', border: 'none', borderBottom: `1px solid ${cara}`,
                    background: sel ? SECTION_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none' }}>
                <Car size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold flex items-center gap-1.5" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5, fontVariantNumeric: 'tabular-nums' }}>
                        {v.spz}
                        {(v.stav === 'servis' || v.aktivni_servis) && <Wrench size={12} style={{ color: muted }} aria-label="V servisu" />}
                        {v.power_v != null && Number(v.power_v) < 12 && <BatteryWarning size={12} style={{ color: CERVENA }} aria-label="Slabá baterie" />}
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>
                        {[siroky ? v.model : null, STAV[st], ridic].filter(Boolean).join(' · ') || v.model}
                    </p>
                </div>
                {siroky && bunka(v.km_dnes ? `${cislo(v.km_dnes, 1)} km` : '—', false, 90)}
                {siroky && bunka(dniText(v.stk_dni_do_expirace), problemy.has(`stk-${v.id}`))}
                {siroky && bunka(dniText(v.pojisteni_dni_do_expirace), problemy.has(`poj-${v.id}`))}
                {siroky && bunka(v.km_do_servisu == null ? '—' : `${cislo(v.km_do_servisu)} km`, problemy.has(`srv-${v.id}`), 110)}
                {!siroky && problem && <AlertTriangle size={15} style={{ color: CERVENA, flexShrink: 0 }} aria-label="K dořešení" />}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat SPZ, model nebo řidiče…', placeholderKratky: 'Hledat' }}
                akce={isMobile ? [] : [{ id: 'nove', label: 'Přidat auto', Icon: Plus, primarni: true, onClick: () => { setVybranyId(null); setPridat(true); } }]}
                nastrojeAkce={isMobile ? null : [
                    { id: 'tank', label: 'Tankování za rok (CSV)', Icon: Download, onClick: exportTankovani },
                    { id: 'srv', label: 'Servis a STK (CSV)', Icon: Download, onClick: exportServis },
                ]}
                pohled={POHLEDY.map((p) => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
                pod={isMobile && kDoreseni.length > 0 ? (
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} styl={{ marginTop: 8 }}
                        renderAkce={(k) => (
                            <button type="button" onClick={() => setVybranyId(k.vozidloId)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                Otevřít auto
                            </button>
                        )} />
                ) : null} />
            {siroky && (
                <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                    <span style={{ width: 16 }} />
                    <span className="flex-1">Auto</span>
                    <span style={{ width: 90, textAlign: 'right' }}>Dnes</span>
                    <span style={{ width: 96, textAlign: 'right' }}>STK</span>
                    <span style={{ width: 96, textAlign: 'right' }}>Pojištění</span>
                    <span style={{ width: 110, textAlign: 'right' }}>Do servisu</span>
                </div>
            )}
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {vozidla.length === 0 ? 'Zatím žádná auta — přidej první.' : q ? 'Nic se neshoduje.' : 'V tomhle pohledu nic není.'}
                    </p>
                )}
                {seznam.map((v) => radek(v, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    // ── Detail auta ──
    const detailPanel = () => {
        const v = vybrane;
        if (!v) return null;
        const ridic = v.aktualni_ridic_id ? ridiciMap.get(v.aktualni_ridic_id) : null;
        const tlacitko = (Icon, label, druh) => (
            <button type="button" onClick={() => setModal({ druh, vozidlo: v })} className="inline-flex items-center gap-1.5 rounded-lg"
                style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                <Icon size={14} /> {label}
            </button>
        );
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '6px 14px 0' : '16px 18px 0', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na auta" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-bold flex items-center gap-2" style={{ fontSize: isMobile ? 19 : 18, margin: 0, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', letterSpacing: 0.5 }}>
                                {v.spz}
                                {(v.stav === 'servis' || v.aktivni_servis) && <Wrench size={15} style={{ color: muted }} aria-label="V servisu" />}
                            </h2>
                            <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>
                                {[v.model, v.rok_vyroby, v.typ_paliva, STAV[classifyVozidlo(v)], ridic ? (ridic.cele_jmeno || `${ridic.jmeno} ${ridic.prijmeni}`) : null].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                        {!isMobile && zalozka === 'tankovani' && tlacitko(Fuel, 'Tankování', 'tankovani')}
                        {!isMobile && zalozka === 'servis' && tlacitko(Wrench, 'Servis', 'servis')}
                        {!isMobile && zalozka === 'pojisteni' && tlacitko(ShieldCheck, 'Smlouva', 'pojisteni')}
                    </div>
                    <div className="flex" style={{ gap: 4, marginTop: 10, overflowX: 'auto' }}>
                        {ZALOZKY.filter((z) => !(isMobile && z.id === 'nastaveni')).map((z) => {
                            const a = zalozka === z.id;
                            return (
                                <button key={z.id} type="button" onClick={() => setZalozka(z.id)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: isMobile ? 44 : 36, padding: '0 10px',
                                        fontSize: 12.5, fontWeight: 600, color: a ? 'var(--text-primary)' : muted, borderBottom: `2px solid ${a ? SECTION_COLOR : 'transparent'}`, marginBottom: -1 }}>
                                    {z.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                    {zalozka === 'prehled' && (
                        <InspectorPrehled vozidlo={v} ridic={ridic} isDark={isDark}
                            onAddTankovani={(x) => setModal({ druh: 'tankovani', vozidlo: x })} onAddServis={(x) => setModal({ druh: 'servis', vozidlo: x })} />
                    )}
                    {zalozka === 'tankovani' && <TankovaniAuta vozidlo={v} obnova={obnova} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara} />}
                    {zalozka === 'servis' && <ServisAuta vozidlo={v} obnova={obnova} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara} />}
                    {zalozka === 'pojisteni' && <PojisteniAuta vozidlo={v} obnova={obnova} isDark={isDark} cardStyle={cardStyle} muted={muted} />}
                    {zalozka === 'nastaveni' && <InspectorNastaveni vozidlo={v} isDark={isDark} onSave={refetch} />}
                    {isMobile && ['tankovani', 'servis', 'pojisteni'].includes(zalozka) && (
                        <button type="button" onClick={() => setModal({ druh: zalozka, vozidlo: v })} className="w-full rounded-xl font-semibold"
                            style={{ height: 44, marginTop: 12, background: SECTION_COLOR, color: '#0A0612', border: 'none', cursor: 'pointer' }}>
                            {zalozka === 'tankovani' ? 'Zapsat tankování' : zalozka === 'servis' ? 'Zapsat servis' : 'Přidat smlouvu'}
                        </button>
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const panel = () => <PanelVozovyPark vozidla={vozidla} kDoreseni={kDoreseni} onOtevri={setVybranyId} cardStyle={cardStyle} muted={muted} cara={cara} isDark={isDark} />;

    const poUlozeni = () => { refetch?.(); setModal(null); setObnova((o) => o + 1); };

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />

            {pridat && (
                <SidePanel open onClose={() => setPridat(false)} title={null} modal={false} width={460} isDark={isDark} isMobile={isMobile} accent={SECTION_COLOR} topOffset={top}>
                    <InspectorCreate onClose={() => setPridat(false)} isDark={isDark}
                        onSaved={(created) => { refetch?.(); setPridat(false); if (created?.id) setVybranyId(created.id); }} />
                </SidePanel>
            )}

            {modal?.druh === 'tankovani' && (
                <TankovaniModal vozidlo={modal.vozidlo} ridic={modal.vozidlo.aktualni_ridic_id ? ridiciMap.get(modal.vozidlo.aktualni_ridic_id) : null}
                    onClose={() => setModal(null)} onSaved={poUlozeni} isDark={isDark} isMobile={isMobile} cardStyle={cardStyle} />
            )}
            {modal?.druh === 'servis' && <ServisModal vozidlo={modal.vozidlo} onClose={() => setModal(null)} onSaved={poUlozeni} isDark={isDark} isMobile={isMobile} />}
            {modal?.druh === 'pojisteni' && <PojistniModal vozidlo={modal.vozidlo} onClose={() => setModal(null)} onSaved={poUlozeni} isDark={isDark} isMobile={isMobile} />}
        </div>
    );
}

/** Třetí sloupec: vozový park v číslech + „K dořešení" (norma §1f). */
function PanelVozovyPark({ vozidla, kDoreseni, onOtevri, cardStyle, muted, cara, isDark }) {
    const [kpi, setKpi] = useState({});
    useEffect(() => {
        const d = new Date();
        const od = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
        const dnes = d.toISOString().slice(0, 10);
        Promise.allSettled([
            nacti(`/api/flotila/tankovani-kpi?datum_od=${od}&datum_do=${dnes}`),
            nacti('/api/flotila/servis-kpi'),
            nacti('/api/flotila/pojisteni-kpi'),
        ]).then(([t, s, p]) => setKpi({
            tank: t.status === 'fulfilled' ? t.value : null,
            servis: s.status === 'fulfilled' ? s.value : null,
            poj: p.status === 'fulfilled' ? p.value : null,
        }));
    }, []);
    const r = (l, h) => (
        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
            <span style={{ color: muted }}>{l}</span><b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{h}</b>
        </div>
    );
    const nadpis = (t, mt = 0) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: `${mt}px 0 6px` }}>{t}</p>;
    return (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
            {nadpis('Vozový park')}
            {r('Aut', vozidla.length)}
            {r('Na cestě', vozidla.filter((v) => classifyVozidlo(v) === 'driving').length)}
            {r('V servisu', vozidla.filter((v) => v.stav === 'servis' || v.aktivni_servis).length)}
            {nadpis('Náklady', 16)}
            {r('Tankování tento měsíc', kc(kpi.tank?.naklady))}
            {r('Natankováno', kpi.tank?.litry != null ? `${cislo(kpi.tank.litry)} l` : '—')}
            {r('Servis za 12 měsíců', kc(kpi.servis?.naklady_12m))}
            {r('Pojistné ročně', kc(kpi.poj?.rocni_pojistne))}
            {nadpis('K dořešení', 16)}
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne="U aut je všechno v pořádku."
                renderAkce={(k) => (
                    <button type="button" onClick={() => onOtevri(k.vozidloId)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                        Otevřít
                    </button>
                )} />
        </div>
    );
}

function Radky({ nacitam, prazdne, muted, children }) {
    if (nacitam) return <p className="text-sm" style={{ color: muted }}>Načítám…</p>;
    if (!children?.length) return <p className="text-sm" style={{ color: muted }}>{prazdne}</p>;
    return <div>{children}</div>;
}

function TankovaniAuta({ vozidlo, obnova, isMobile, muted, cara }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        nacti(`/api/flotila/tankovani?vozidlo_id=${vozidlo.id}`).then((d) => setData(Array.isArray(d) ? d : [])).catch(() => setData([]));
    }, [vozidlo.id, obnova]);
    return (
        <Radky nacitam={data === null} prazdne="Zatím žádné tankování." muted={muted}>
            {(data || []).map((t) => {
                const celkem = Number(t.litry) * Number(t.cena_kc_litr);
                const kdo = t.zamestnanec ? `${t.zamestnanec.jmeno} ${t.zamestnanec.prijmeni}` : null;
                return (
                    <div key={t.id} className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 52 : 40, borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
                        <span style={{ width: 84, color: muted, fontSize: 12 }}>{datum(t.datum)}</span>
                        <span className="flex-1 min-w-0 truncate" style={{ color: muted, fontSize: 12 }}>
                            {[`${cislo(t.litry, 1)} l`, t.cena_kc_litr ? `${cislo(t.cena_kc_litr, 2)} Kč/l` : null, t.km_stav ? `${cislo(t.km_stav)} km` : null, kdo, t.stav === 'ceka_sken' ? 'čeká na sken' : null].filter(Boolean).join(' · ')}
                        </span>
                        <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{Number.isFinite(celkem) ? kc(celkem) : '—'}</b>
                    </div>
                );
            })}
        </Radky>
    );
}

function ServisAuta({ vozidlo, obnova, isMobile, muted, cara }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        setData(null);
        nacti(`/api/flotila/servis/feed?vozidlo_id=${vozidlo.id}`).then((d) => setData(Array.isArray(d) ? d : [])).catch(() => setData([]));
    }, [vozidlo.id, obnova]);
    const STAV = { planovano: 'plán', probiha: 'probíhá', dokonceno: null };
    return (
        <Radky nacitam={data === null} prazdne="Zatím žádný servis ani STK." muted={muted}>
            {(data || []).map((s) => (
                <div key={`${s.zdroj || 's'}-${s.id}`} className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 52 : 40, borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
                    <span style={{ width: 84, color: muted, fontSize: 12 }}>{datum(s.datum)}</span>
                    <span className="flex-1 min-w-0">
                        <span className="block truncate" style={{ color: 'var(--text-primary)' }}>
                            {(Array.isArray(s.typy) && s.typy.length ? s.typy.map((x) => x.nazev).join(', ') : (s.typ || '').toUpperCase())}
                        </span>
                        <span className="block truncate" style={{ color: muted, fontSize: 11.5 }}>
                            {[s.popis, s.servis, s.km_stav ? `${cislo(s.km_stav)} km` : null, STAV[s.stav]].filter(Boolean).join(' · ')}
                        </span>
                    </span>
                    <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{s.cena_kc != null ? kc(s.cena_kc) : '—'}</b>
                </div>
            ))}
        </Radky>
    );
}

function PojisteniAuta({ vozidlo, obnova, isDark, cardStyle, muted }) {
    const [smlouvy, setSmlouvy] = useState(null);
    useEffect(() => {
        setSmlouvy(null);
        nacti(`/api/flotila/pojisteni/vozidlo/${vozidlo.id}`).then((d) => setSmlouvy(Array.isArray(d?.smlouvy) ? d.smlouvy : [])).catch(() => setSmlouvy([]));
    }, [vozidlo.id, obnova]);
    if (smlouvy === null) return <p className="text-sm" style={{ color: muted }}>Načítám…</p>;
    if (!smlouvy.length) return <p className="text-sm" style={{ color: muted }}>Žádná pojistná smlouva.</p>;
    return <PojistniKarta vozidlo={vozidlo} smlouvy={smlouvy} cardStyle={cardStyle} isDark={isDark} />;
}
